"use client";

import { Check, Star } from "lucide-react";
import { Button } from "../ui/button";
import { Link } from "wouter";
import { Switch } from "../ui/switch";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import NumberFlow from "@number-flow/react";
import React from "react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";

interface PricingPlan {
  name: string;
  price: string;
  yearlyPrice: string;
  period: string;
  features: string[];
  description: string;
  buttonText: string;
  monthlyPriceId: string;
  yearlyPriceId: string;
  isPopular: boolean;
  isComingSoon?: boolean;
}

const plans: PricingPlan[] = [
  {
    name: "Solo",
    price: "0",
    yearlyPrice: "0",
    period: "per month",
    features: [
      "Up to 5 clients",
      "Time tracking",
      "Standard Invoice Template",
      "Email support"
    ],
    description: "Everything you need to get started",
    buttonText: "Sign Up Free",
    monthlyPriceId: "",
    yearlyPriceId: "",
    isPopular: false,
  },
  {
    name: "Pro",
    price: "10",
    yearlyPrice: "7",
    period: "per month",
    features: [
      "Unlimited clients",
      "Metrics Dashboard",
      "Customizable invoices",
      "Priority support"
    ],
    description: "Perfect for freelancers growing their business",
    buttonText: "Start trial",
    monthlyPriceId: "price_1RJIy0KC0x0Qg4vJqwpLtIjT",
    yearlyPriceId: "price_1RJDPtKC0x0Qg4vJiLu33TUe",
    isPopular: true,
  },
  {
    name: "Team",
    price: "49",
    yearlyPrice: "39",
    period: "per month",
    features: [
      "Everything in Pro, plus:",
      "Team management",
      "Advanced reporting",
      "Custom integrations"
    ],
    description: "For consulting teams",
    buttonText: "Coming Soon",
    monthlyPriceId: "",
    yearlyPriceId: "",
    isPopular: false,
    isComingSoon: true,
  },
];

export const PricingContent = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const switchRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();
  const { tier: userTier, isLoading: isLoadingSubscription } = useSubscription();
  
  const isAuthenticated = !!user;
  
  const handleToggle = (checked: boolean) => {
    setIsMonthly(!checked);
  };

  // Create a better implementation for the Stripe checkout
  const redirectToStripe = (priceId: string) => {
    // Use Stripe's recommended approach - redirect to their dashboard checkout page
    // This is a temporary solution - ideally should be handled server-side
    
    // Map price IDs to their corresponding checkout URLs from Stripe Dashboard
    const checkoutUrls: Record<string, string> = {
      'price_1RTueIKC0x0Qg4vJMsrBMOuk': 'https://buy.stripe.com/eVqfZi4GZg6wamp53KcIE08', // Pro Monthly
      'price_1RTufIKC0x0Qg4vJhZznQmnr': 'https://buy.stripe.com/dRm14o4GZ3jK8eheEkcIE07', // Pro Yearly
    };
    
    // Get the checkout URL for the selected price
    const checkoutUrl = checkoutUrls[priceId];
    
    if (checkoutUrl) {
      console.log(`Redirecting to Stripe Dashboard checkout: ${checkoutUrl}`);
      window.location.href = checkoutUrl;
    } else {
      // Fallback to Stripe's product page if we don't have a direct link
      console.log(`No direct checkout URL for price ID: ${priceId}, redirecting to Stripe`);
      window.location.href = 'https://stripe.com/payments';
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Simple, transparent pricing
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Choose the plan that fits your needs. All plans include a 14-day free trial.
        </p>
      </div>

      <div className="flex justify-center">
        <label className="relative inline-flex items-center cursor-pointer">
          <span className="mr-2 font-semibold text-foreground">Monthly</span>
          <Switch
            ref={switchRef as any}
            checked={!isMonthly}
            onCheckedChange={handleToggle}
            className="relative"
          />
          <span className="ml-2 font-semibold text-foreground">
            Annual <span className="text-primary">(Save 30%)</span>
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 sm:2 gap-4 max-w-5xl mx-auto">
        {plans.map((plan, index) => {
          // Check if this is the user's current plan
          const isCurrentPlan = isAuthenticated && !isLoadingSubscription && plan.name.toLowerCase() === userTier;
          
          return (
          <motion.div
            key={index}
            initial={{ y: 50, opacity: 1 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{
              duration: 1.6,
              type: "spring",
              stiffness: 100,
              damping: 30,
              delay: 0.4,
              opacity: { duration: 0.5 },
            }}
            className={cn(
              `rounded-2xl border-[1px] p-6 bg-card text-card-foreground text-center lg:flex lg:flex-col lg:justify-center relative`,
              plan.isPopular ? "border-primary border-2" : "border-border dark:border-white/20",
              "flex flex-col shadow-sm hover:shadow-md transition-shadow",
              !plan.isPopular && "mt-5",
              index === 0 || index === 2
                ? "z-0 transform translate-x-0 translate-y-0 -translate-z-[50px] rotate-y-[10deg]"
                : "z-10",
              index === 0 && "origin-right",
              index === 2 && "origin-left"
            )}
          >
            {plan.isPopular && (
              <div className="absolute top-0 right-0 bg-primary py-0.5 px-2 rounded-bl-xl rounded-tr-xl flex items-center">
                <Star className="text-primary-foreground h-4 w-4 fill-current" />
                <span className="text-primary-foreground ml-1 font-sans font-semibold">
                  Most Popular
                </span>
              </div>
            )}
            
            <div className="flex-1 flex flex-col">
              <p className="text-base font-semibold text-foreground">
                {plan.name}
              </p>
              <div className="mt-6 flex items-center justify-center gap-x-2">
                <span className="text-5xl font-bold tracking-tight text-foreground">
                  <NumberFlow
                    value={
                      isMonthly ? Number(plan.price) : Number(plan.yearlyPrice)
                    }
                    format={{
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }}
                    transformTiming={{
                      duration: 500,
                      easing: "ease-out",
                    }}
                    willChange
                    className="font-variant-numeric: tabular-nums"
                  />
                </span>
                <span className="text-sm font-semibold leading-6 tracking-wide text-muted-foreground">
                  / {plan.period}
                </span>
              </div>

              <p className="text-xs leading-5 text-muted-foreground">
                {isMonthly ? "billed monthly" : "billed annually"}
              </p>

              <ul className="mt-6 space-y-3">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-left text-foreground dark:text-white">{feature}</span>
                  </li>
                ))}
              </ul>

              <hr className="w-full my-6 border-border" />

              {/* Only show buttons for authenticated users */}
              {isAuthenticated && (
                <>
                  {plan.isComingSoon ? (
                    <Button 
                      variant="outline"
                      className={cn(
                        "group relative w-full gap-2 overflow-hidden text-lg font-semibold tracking-tighter opacity-70 cursor-not-allowed",
                        "bg-card text-foreground border-primary text-primary"
                      )}
                      disabled
                    >
                      {plan.buttonText}
                    </Button>
                  ) : isCurrentPlan ? (
                    <Button
                      variant="outline"
                      className={cn(
                        "group relative w-full gap-2 overflow-hidden text-lg font-semibold tracking-tighter cursor-default",
                        plan.isPopular 
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-green-500 dark:bg-green-600 text-white border-green-500 dark:border-green-600"
                      )}
                      disabled
                    >
                      Current Plan
                    </Button>
                  ) : plan.name === "Solo" ? (
                    <Button 
                      variant="outline"
                      className={cn(
                        "group relative w-full gap-2 overflow-hidden text-lg font-semibold tracking-tighter",
                        "transform-gpu ring-offset-current transition-all duration-300 ease-out hover:ring-2 hover:ring-primary hover:ring-offset-1 hover:bg-primary hover:text-primary-foreground",
                        "bg-card text-foreground border-primary text-primary"
                      )}
                      onClick={() => window.location.href = "/account/settings"}
                    >
                      Downgrade
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      className={cn(
                        "group relative w-full gap-2 overflow-hidden text-lg font-semibold tracking-tighter",
                        "transform-gpu ring-offset-current transition-all duration-300 ease-out hover:ring-2 hover:ring-primary hover:ring-offset-1 hover:bg-primary hover:text-primary-foreground",
                        plan.isPopular
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card border-primary text-primary"
                      )}
                      onClick={() => redirectToStripe(isMonthly ? plan.monthlyPriceId : plan.yearlyPriceId)}
                    >
                      {userTier === 'solo' ? 'Upgrade' : plan.buttonText}
                    </Button>
                  )}
                </>
              )}
              
              <p className="mt-4 text-sm leading-5 text-muted-foreground dark:text-gray-300">
                {plan.description}
              </p>
            </div>
          </motion.div>
        )})}
      </div>

      {/* Single Get Started button for unauthenticated users */}
      {!isAuthenticated && (
        <div className="flex justify-center mt-8">
          <Link href="/login?tab=register">
            <Button 
              size="lg"
              className={cn(
                "px-8 py-3 text-lg font-semibold tracking-tighter",
                "transform-gpu ring-offset-current transition-all duration-300 ease-out hover:ring-2 hover:ring-primary hover:ring-offset-1",
                "bg-primary text-primary-foreground hover:bg-primary/90"
              )}
            >
              Get Started
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}; 